import React from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, Heart, Users, Shield, Target } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: "Patient-Centered Care",
      description: "Every decision we make starts with the patient. Your health, comfort, and convenience are our top priorities."
    },
    {
      icon: Shield,
      title: "Privacy & Security",
      description: "We use industry-leading safeguards to protect your personal health information and ensure confidential care."
    },
    {
      icon: Users,
      title: "Accessibility",
      description: "Quality healthcare should be available to everyone, everywhere. We make it easy and affordable to get the care you need."
    },
    {
      icon: Target,
      title: "Clinical Excellence",
      description: "Our board-certified providers deliver evidence-based care with compassion and expertise."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F4F6F8] to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold text-[#12324A] mb-6">
                About TouchScreenMD
              </h1>
              <p className="text-xl text-[#5F6C7B] mb-6 leading-relaxed">
                We're transforming healthcare by making quality medical care accessible to everyone, everywhere, anytime.
              </p>
              <p className="text-lg text-[#5F6C7B] leading-relaxed">
                TouchScreenMD connects patients with licensed healthcare providers through secure video visits. Whether you need urgent care, primary care, or ongoing management for chronic conditions, our platform makes it simple to get the care you need.
              </p>
            </div>
            <div className="bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] rounded-2xl p-12 text-white">
              <div className="space-y-6">
                <div>
                  <div className="text-5xl font-bold mb-2">24/7</div>
                  <div className="text-lg">Healthcare access anytime</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">50</div>
                  <div className="text-lg">States served nationwide</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">&lt;10min</div>
                  <div className="text-lg">Average wait time</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-6">
            Our Mission
          </h2>
          <p className="text-xl text-[#5F6C7B] leading-relaxed">
            To make quality healthcare accessible, affordable, and convenient for everyone. We believe that getting medical care shouldn't require taking time off work, sitting in waiting rooms, or choosing between your health and your budget. Through innovative telehealth technology and compassionate providers, we're making healthcare work for real people and real lives.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Our Values
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-[#1FB6B2] hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] flex items-center justify-center mb-6">
                    <Icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-2xl font-semibold text-[#12324A] mb-4">{value.title}</h3>
                  <p className="text-[#5F6C7B] leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How We're Different Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Why Choose TouchScreenMD?
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              We're not just another telehealth platform
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] flex items-center justify-center mx-auto mb-6">
                <Shield className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-semibold text-[#12324A] mb-3">Licensed Providers</h3>
              <p className="text-[#5F6C7B]">
                All our providers are board-certified and licensed in your state. Real doctors, real credentials.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] flex items-center justify-center mx-auto mb-6">
                <Heart className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-semibold text-[#12324A] mb-3">Compassionate Care</h3>
              <p className="text-[#5F6C7B]">
                We treat you like a person, not a number. Our providers take time to listen and understand.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] flex items-center justify-center mx-auto mb-6">
                <Users className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-semibold text-[#12324A] mb-3">Transparent Pricing</h3>
              <p className="text-[#5F6C7B]">
                No surprises, no hidden fees. You'll always know what you're paying before your visit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#12324A] mb-4">
              Our Provider Network
            </h2>
            <p className="text-xl text-[#5F6C7B] max-w-2xl mx-auto">
              Board-certified physicians, nurse practitioners, and physician assistants dedicated to your health
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-12 border border-gray-200 text-center">
            <p className="text-lg text-[#5F6C7B] leading-relaxed mb-8">
              Our providers come from diverse backgrounds and specialties, but they all share a commitment to delivering compassionate, evidence-based care. Each provider in our network is:
            </p>
            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#1FB6B2] mt-2 flex-shrink-0"></div>
                <span className="text-[#5F6C7B]">Board-certified in their specialty</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#1FB6B2] mt-2 flex-shrink-0"></div>
                <span className="text-[#5F6C7B]">Licensed in multiple states</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#1FB6B2] mt-2 flex-shrink-0"></div>
                <span className="text-[#5F6C7B]">Experienced in telehealth</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#1FB6B2] mt-2 flex-shrink-0"></div>
                <span className="text-[#5F6C7B]">Committed to continuing education</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#1FB6B2] mt-2 flex-shrink-0"></div>
                <span className="text-[#5F6C7B]">Vetted through rigorous credentialing</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 rounded-full bg-[#1FB6B2] mt-2 flex-shrink-0"></div>
                <span className="text-[#5F6C7B]">Passionate about patient care</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F4C81] to-[#12324A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Experience the TouchScreenMD Difference
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Join thousands of patients who trust us for their healthcare needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-white text-[#0F4C81] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
            >
              Start a Visit Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#0F4C81] px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
